import React, { useCallback, useContext, useState } from 'react';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import {
  useBreakpoint,
  Link,
  Pagination,
  PaginationDisplayedItemCount
} from '@one-thd/sui-atomic-components';
import { useEcoRebatesApi } from '../hooks/use-ecorebates-api';
import { DEFAULT_PAGE_SIZE, EMPTY_FILTER_DATA } from '../utils/constants';
import { RebateConsumerTabs } from './subcomponents/rebate-consumer-tabs/RebateConsumerTabs';
import { RebatePod } from './subcomponents/rebate-pod/RebatePod';
import { RebateSearchAndFilter } from './subcomponents/rebate-search-and-filter/RebateSearchAndFilter';

const RebateListings = () => {
  const { storeZip } = useStore();
  const { localStoreZip } = useContext(ExperienceContext);
  const [filterOptions, setFilterOptions] = useState([]);
  const [filterData, setFilterData] = useState(EMPTY_FILTER_DATA);
  const [zipCode, setZipCode] = useState(storeZip || localStoreZip);
  const [zipCodeChanged, setZipCodeChanged] = useState(false);
  const [consumerType, setConsumerType] = useState('residential');
  const [pageNumber, setPageNumber] = useState(1);
  const matches = useBreakpoint('sm');

  const { data, loading } = useEcoRebatesApi({
    consumerType,
    filterData,
    pageNumber,
    pageSize: DEFAULT_PAGE_SIZE,
    zipCode
  });

  const onZipCodeChange = useCallback((newZipCode) => {
    setZipCode(newZipCode);
    setFilterData(EMPTY_FILTER_DATA);
    setZipCodeChanged(true);
  }, []);

  const onPageChanged = useCallback((event, newPageNumber) => {
    event.preventDefault();
    event.stopPropagation();
    setPageNumber(newPageNumber);
    document.getElementById('rebate-listings').scrollIntoView();
  }, []);

  const shouldUpdateFilters = (!filterOptions?.length && !loading)
    || (zipCodeChanged && data?.area?.zipCode?.toString() === zipCode);

  if (shouldUpdateFilters) {
    const formattedFilterOptions = data?.filters?.filter(
      // TODO Checking against label is wrong. Should check against an id instead.
      (filterOption) => filterOption.label !== 'Rebate Consumer'
    );
    setFilterOptions(formattedFilterOptions || []);
    setZipCodeChanged(false);
  }

  const rebateProducts = data?.productRebateDetails || [];
  const localAreaData = data?.area || {};
  const pageCount = data?.pageCount || null;
  const itemCount = data?.totalItemCount || null;
  const showPagination = pageCount !== null && pageCount !== undefined;

  return (
    <div id="rebate-listings" data-component="RebateListings">
      <RebateConsumerTabs
        onChange={setConsumerType}
      />

      <RebateSearchAndFilter
        filterOptions={filterOptions}
        filterData={filterData}
        setFilterData={setFilterData}
        localAreaData={localAreaData}
        setLocalZip={onZipCodeChange}
        totalItemCount={itemCount}
      />

      {rebateProducts.map((rebateProduct, rebateIndex) => (
        <RebatePod
          rebateProduct={rebateProduct}
          key={rebateIndex}
          index={rebateIndex}
        />
      ))}

      {showPagination && (
        <>
          <div className="sui-flex sui-py-2">
            <Link
              className="sui-flex lg:sui-justify-end sui-justify-center sui-w-full lg:sui-mr-2 sui-mr-0"
              href="https://www.ecorebates.com/terms-of-use"
            >EcoRebates Terms &#169; 2021
            </Link>
          </div>
          <div className="sui-grow">
            <div
              className={'sui-relative sui-flex sui-items-center sui-gap-4 sui-flex-col-reverse'
                + ' sm:sui-flex-row sm:sui-gap-0 lg:sui-mr-[-10px] sui-mr-0 lg:sui-ml-2 sui-ml-0'}
            >
              <PaginationDisplayedItemCount
                page={pageNumber}
                itemsPerPage={DEFAULT_PAGE_SIZE}
                items={itemCount}
              />
              <Pagination
                count={pageCount}
                page={pageNumber}
                onChange={onPageChanged}
                fullWidth={matches.lesser}
                siblingCount={matches.lesser ? 0 : 1}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

RebateListings.propTypes = {};

RebateListings.displayName = 'RebateListings';

RebateListings.defaultProps = {};

export { RebateListings };
